<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import noImageThumb from '../../../../../assets/images/no-image.png'

interface Image {
  id: string | number
  [key: string]: any // Allow any field name
}

const props = defineProps<{
  images: Image[]
  currentIndex: number
  imageField?: string // Field name for image URL
  captionField?: string // Field name for caption
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'navigate', index: number): void
}>()

const showControls = ref(false)

const getImageUrl = (image: Image) => {
  return image[props.imageField || 'src'] || noImageThumb
}

const getCaption = (image: Image) => {
  return image[props.captionField || 'caption'] || ''
}

const currentImage = computed(() => props.images[props.currentIndex])

const handlePrevious = () => {
  if (props.currentIndex > 0) {
    emit('navigate', props.currentIndex - 1)
  }
}

const handleNext = () => {
  if (props.currentIndex < props.images.length - 1) {
    emit('navigate', props.currentIndex + 1)
  }
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    emit('close')
  } else if (event.key === 'ArrowLeft') {
    handlePrevious()
  } else if (event.key === 'ArrowRight') {
    handleNext()
  }
}

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.src = noImageThumb
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<template>
  <div 
    class="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
    @click="emit('close')"
    @mousemove="showControls = true"
    @mouseleave="showControls = false"
  >
    <!-- Image Container -->
    <div 
      class="relative max-w-[90vw] max-h-[90vh]"
      @click.stop
    >
      <img
        :src="getImageUrl(currentImage)"
        :alt="currentImage.alt || ''"
        @error="handleImageError"
        class="max-w-full max-h-[90vh] object-contain"
      />
      
      <!-- Caption -->
      <div 
        v-if="getCaption(currentImage)"
        class="absolute bottom-0 left-0 right-0 p-4 bg-black/50 text-white text-center transition-opacity duration-300"
        :class="{ 'opacity-0': !showControls }"
      >
        {{ getCaption(currentImage) }}
      </div>
    </div>

    <!-- Navigation Controls -->
    <div 
      class="absolute inset-x-0 bottom-8 flex items-center justify-center gap-4 transition-opacity duration-300"
      :class="{ 'opacity-0': !showControls }"
    >
      <!-- Previous Button -->
      <button
        v-if="props.currentIndex > 0"
        @click.stop="handlePrevious"
        class="p-2 bg-black/50 text-white rounded-full hover:bg-black/75 transition-colors"
        title="Previous image"
      >
        ←
      </button>

      <!-- Image Counter -->
      <div class="px-4 py-2 bg-black/50 text-white rounded-full">
        {{ props.currentIndex + 1 }} / {{ props.images.length }}
      </div>

      <!-- Next Button -->
      <button
        v-if="props.currentIndex < props.images.length - 1"
        @click.stop="handleNext"
        class="p-2 bg-black/50 text-white rounded-full hover:bg-black/75 transition-colors"
        title="Next image"
      >
        →
      </button>
    </div>

    <!-- Close Button -->
    <button
      @click="emit('close')"
      class="absolute top-4 right-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/75 transition-colors"
      :class="{ 'opacity-0': !showControls }"
    >
      ✕
    </button>
  </div>
</template>